// Users tab — the install's people as docs knows them from zehn-id
// (`GET /api/v1/settings/users`): status, zehn-id groups (what the group
// grants in Settings turn into docs' permissions), last activity and when
// zehn-id's directory last updated them. People are managed in zehn-id, so
// the table is read-only.

import { useTranslation } from 'react-i18next';
import { Loader2, Search as SearchIcon, Users } from 'lucide-react';
import moment from 'moment';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ep } from '../lib/api.js';
import { usePagedList } from '../lib/usePagedList.js';
import { ListPagination } from './ListPagination.jsx';

const STATUS_TONE = {
  active: 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 border-green-200 dark:border-green-800',
  suspended: 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border-red-200 dark:border-red-800',
};

function fmt(value) {
  return value ? moment(value).format('DD MMM YYYY HH:mm') : '—';
}

export const UsersTab = () => {
  const { t } = useTranslation('console');
  const list = usePagedList(ep.users(), { size: 25 });
  const items = list.items;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">{t('users.intro')}</p>
        <div className="relative flex-1 min-w-56 max-w-md">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={list.searchInput} onChange={(e) => list.setSearchInput(e.target.value)} placeholder={t('users.searchPlaceholder')} className="pl-9" />
        </div>
      </div>

      {list.loading && items.length === 0 ? (
        <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 text-blue-500 dark:text-blue-400 animate-spin" /></div>
      ) : items.length > 0 ? (
        <>
          <div className="w-full border rounded-lg overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('users.table.name')}</TableHead>
                  <TableHead>{t('users.table.email')}</TableHead>
                  <TableHead className="w-28">{t('users.table.status')}</TableHead>
                  <TableHead>{t('users.table.groups')}</TableHead>
                  <TableHead className="w-40 text-center">{t('users.table.lastSeen')}</TableHead>
                  <TableHead className="w-40 text-center">{t('users.table.synced')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((u) => (
                  <TableRow key={u.uuid} data-row-id={u.uuid}>
                    <TableCell className="font-medium">
                      {u.display_name || <span className="text-muted-foreground">{t('users.noName')}</span>}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">{u.email || '—'}</TableCell>
                    <TableCell>
                      <span className={`inline-flex rounded-full border px-2 py-0.5 text-xs ${STATUS_TONE[u.status] || 'bg-muted/50'}`}>
                        {t(`users.statuses.${u.status}`, { defaultValue: u.status })}
                      </span>
                    </TableCell>
                    <TableCell>
                      {u.groups?.length ? (
                        <div className="flex flex-wrap gap-1">
                          {u.groups.map((g) => (
                            <span key={g} className="inline-flex rounded-full border bg-muted/50 px-2 py-0.5 font-mono text-xs">{g}</span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground">{t('users.noGroups')}</span>
                      )}
                    </TableCell>
                    <TableCell className="text-center text-sm text-muted-foreground" title={u.last_seen_at ? moment(u.last_seen_at).fromNow() : undefined}>
                      {u.last_seen_at ? fmt(u.last_seen_at) : t('users.neverSeen')}
                    </TableCell>
                    <TableCell className="text-center text-sm text-muted-foreground">{fmt(u.directory_synced_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <ListPagination list={list} />
        </>
      ) : (
        <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
          <Users className="h-12 w-12 mb-3 text-muted-foreground/60" />
          <p className="text-lg font-medium">{list.hasFilters ? t('list.noMatch') : t('users.empty.title')}</p>
          <p className="text-sm text-muted-foreground">{list.hasFilters ? t('list.noMatchHint') : t('users.empty.description')}</p>
          {list.hasFilters && <Button variant="ghost" size="sm" className="mt-2" onClick={list.clearFilters}>{t('list.clearFilters')}</Button>}
        </div>
      )}
    </div>
  );
};
